import Phaser from 'phaser';
import { PALETTE, PANEL_BG } from '../theme';
import { getMainCatalog } from '../turn/scenarioCatalog';
import { validateScenario } from '../turn/scenarioValidator';
import { FACTIONS, PLAYABLE_FACTIONS, applyFlagStyle, FACTION_UNIQUE_UNITS, createFlagElement } from '../turn/factions';
import { renderMinimapCanvas } from '../turn/minimap';
import { UNIT_ICON_PATHS } from '../turn/unitIcons';
import type { Scenario, PlayerId, GameConfig, FactionPick } from '../turn/types';
import { audio } from '../audio/instances';
import { t, localize } from '../i18n';

const PLAYER_COLORS = ['#4a90e2', '#d2483a', '#3a9a4f', '#d4a017', '#9b59b6', '#e67e22'];

/** Carousel items: random + all characters from all factions. */
const CAROUSEL_ITEMS: { key: string; factionId: string | null; charId: string | null }[] = [
  { key: 'random', factionId: null, charId: null },
  ...PLAYABLE_FACTIONS.flatMap((f) =>
    f.characters.map((c) => ({ key: `${f.id}:${c.id}`, factionId: f.id, charId: c.id })),
  ),
];

function scenarioDisplayName(sc: Scenario): string {
  return localize(sc.name);
}

function scenarioDescription(sc: Scenario): string {
  return localize(sc.description);
}

export class MapSelectScene extends Phaser.Scene {
  private dom: HTMLDivElement | null = null;
  private scenarios: Scenario[] = [];
  /** Set of scenario references that were uploaded this session (vs catalog). */
  private uploadedScenarios = new Set<Scenario>();
  /** Minimap preview size (px) — kept compact so the description has room. */
  private static readonly MINIMAP_SIZE = 180;
  /** Fixed heights so the layout never reflows when the selection changes
   *  (e.g. picking a character swaps short "random" text for a full bio). The
   *  human card and the scenario panel share the top-row height. */
  private static readonly TOP_ROW_HEIGHT = 300;
  /** Cap so the top row doesn't stretch absurdly tall on large screens (FullHD+);
   *  it grows from TOP_ROW_HEIGHT up to here, then stops. */
  private static readonly TOP_ROW_MAX_HEIGHT = 370;
  private static readonly CPU_CARD_HEIGHT = 166;
  private selected: Scenario | null = null;
  /** Scenario panel: floated minimap + title + description that wraps around it. */
  private scenarioInfoContainer: HTMLDivElement | null = null;
  private humanCardContainer: HTMLDivElement | null = null;
  private cpuCardsContainer: HTMLDivElement | null = null;
  private startBtn: HTMLButtonElement | null = null;

  // Slot 0 = human, 1..N-1 = CPU
  private colorSelections: number[] = [];
  /** 'random' | 'factionId:charId' per slot */
  private selections: string[] = [];

  // Card content refs for in-place updates
  private cardContentRefs: (HTMLDivElement | null)[] = [];
  // Pennant elements for sync
  private pennantRefs: { el: HTMLElement; wrapper: HTMLElement; slot: number; colorIndex: number }[] = [];

  // Carousel modal
  private carouselOverlay: HTMLDivElement | null = null;
  private carouselSlot = -1;
  private carouselIndex = 0;
  private carouselCardBox: HTMLDivElement | null = null;
  private carouselKeyHandler: ((e: KeyboardEvent) => void) | null = null;
  /** Right-click quick-pick popup for character selection. */
  private quickPickOverlay: HTMLDivElement | null = null;
  private quickPickOnDocClick: ((e: MouseEvent) => void) | null = null;
  private quickPickOnEsc: ((e: KeyboardEvent) => void) | null = null;
  private carouselAnimating = false;
  private preloadedPortraits: HTMLImageElement[] = [];
  private humanCard: HTMLDivElement | null = null;
  private glowStyleEl: HTMLStyleElement | null = null;

  constructor() {
    super({ key: 'MapSelect' });
  }

  create(): void {
    this.cameras.main.setBackgroundColor(PALETTE.bg);
    this.scenarios = [...getMainCatalog().list()];
    this.resetSelections();
    this.preloadPortraits();
    this.injectGlowAnimation();
    this.dom = this.buildDom();
    document.body.appendChild(this.dom);
    this.updatePreview();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
  }

  private preloadPortraits(): void {
    this.preloadedPortraits = [];
    for (const f of PLAYABLE_FACTIONS) {
      for (const c of f.characters) {
        if (c.portrait) {
          const img = new Image();
          img.src = c.portrait;
          this.preloadedPortraits.push(img);
        }
      }
    }
  }

  private updateHumanCardGlow(): void {
    if (!this.humanCard) return;
    if (this.selections[0] === 'random') {
      this.humanCard.style.animation = 'humanCardGlow 2s ease-in-out infinite';
      this.humanCard.style.borderColor = '#3a6090';
    } else {
      this.humanCard.style.animation = 'none';
      this.humanCard.style.borderColor = '#2a3140';
      this.humanCard.style.boxShadow = 'none';
    }
  }

  private injectGlowAnimation(): void {
    if (this.glowStyleEl) return;
    this.glowStyleEl = document.createElement('style');
    this.glowStyleEl.textContent = `
      @keyframes humanCardGlow {
        0%, 100% { box-shadow: 0 0 8px rgba(74, 144, 226, 0.2); }
        50% { box-shadow: 0 0 18px rgba(74, 144, 226, 0.5); }
      }
    `;
    document.head.appendChild(this.glowStyleEl);
  }

  private resetSelections(): void {
    this.selected = this.scenarios[0] ?? null;
    this.initSelectionsFor(this.selected);
  }

  /** (Re)initialize per-slot color/faction selections for a scenario. When the
   *  scenario forces factions, each slot is pinned to a definite player
   *  (identity color mapping) so the locked faction shown per slot is
   *  deterministic; otherwise colors are auto-assigned (-1) as usual. */
  private initSelectionsFor(sc: Scenario | null): void {
    const n = sc?.players.length ?? 0;
    this.colorSelections = sc?.factions
      ? Array.from({ length: n }, (_, i) => i)
      : new Array(n).fill(-1);
    this.selections = new Array(n).fill('random');
    this.cardContentRefs = [];
    this.pennantRefs = [];
    this.syncForcedFactionSelections();
  }

  /** Pin each forced slot's selection to its scenario-mandated faction so the
   *  card renders the locked faction. No-op for scenarios without `factions`. */
  private syncForcedFactionSelections(): void {
    const sc = this.selected;
    if (!sc?.factions) return;
    for (let slot = 0; slot < this.selections.length; slot++) {
      const c = this.colorSelections[slot];
      const pid = c >= 0 ? sc.players[c] : sc.players[slot];
      const forced = sc.factions[pid];
      if (forced) this.selections[slot] = `${forced}:a`;
    }
  }

  /** The faction id forced on a slot by the scenario, or null if the slot is
   *  freely pickable (no forced faction for its resolved player). */
  private forcedFactionForSlot(slot: number): string | null {
    const sc = this.selected;
    if (!sc?.factions) return null;
    const c = this.colorSelections[slot];
    if (c < 0) return null;
    const pid = sc.players[c];
    return sc.factions[pid] ?? null;
  }

  // ─── DOM skeleton ─────────────────────────────────────────────

  private buildDom(): HTMLDivElement {
    const root = document.createElement('div');
    Object.assign(root.style, {
      position: 'fixed', inset: '0',
      display: 'flex', flexDirection: 'column',
      color: '#d8dee9',
      fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
      background: '#0b0d10 url("wallpaper.png") center/cover no-repeat', zIndex: '10',
    } as Partial<CSSStyleDeclaration>);

    const hoverStyle = document.createElement('style');
    hoverStyle.textContent = `.img-btn:hover{filter:brightness(1.2)}.img-btn:active{filter:brightness(0.9);transform:scale(0.97)}`;
    root.appendChild(hoverStyle);

    // Header
    const header = document.createElement('div');
    Object.assign(header.style, {
      display: 'flex', alignItems: 'center', gap: '16px',
      padding: '14px 20px', borderBottom: '1px solid #2a3140',
    });
    const title = document.createElement('span');
    title.textContent = t('mapSelect.header');
    Object.assign(title.style, { fontSize: '18px', fontWeight: '700', letterSpacing: '2px' });
    header.appendChild(title);
    root.appendChild(header);

    // Body: sidebar + content
    const body = document.createElement('div');
    Object.assign(body.style, { display: 'flex', flex: '1', overflow: 'hidden' });

    // ── Scenario list sidebar ──
    const listPanel = document.createElement('div');
    Object.assign(listPanel.style, {
      width: '220px', minWidth: '220px', borderRight: '1px solid #2a3140',
      overflowY: 'auto', padding: '8px',
    });
    const listHeader = document.createElement('div');
    listHeader.textContent = t('mapSelect.scenarios');
    Object.assign(listHeader.style, {
      fontSize: '11px', fontWeight: '600', textTransform: 'uppercase',
      letterSpacing: '1.5px', opacity: '0.5',
      padding: '6px 14px 8px', marginBottom: '2px',
    });
    listPanel.appendChild(listHeader);

    // Upload-from-disk button — adds the chosen JSON to the in-memory list
    // for this session so the user can play scenarios created in the editor
    // without dropping them into public/scenarios/ first.
    // Hidden for now (custom-scenario upload disabled in the shipped build); the
    // wiring stays intact so it can be re-enabled by flipping the display back.
    const uploadBtn = document.createElement('button');
    uploadBtn.textContent = t('mapSelect.uploadScenario');
    Object.assign(uploadBtn.style, {
      display: 'none', width: 'calc(100% - 8px)',
      margin: '0 4px 8px', padding: '8px 10px',
      background: '#1a2030', color: '#d8dee9',
      border: '1px dashed #3a5078', borderRadius: '6px',
      cursor: 'pointer', fontFamily: 'inherit',
      fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px',
      transition: 'background 0.15s, border-color 0.15s',
    });
    uploadBtn.addEventListener('mouseenter', () => {
      uploadBtn.style.background = '#202a40';
      uploadBtn.style.borderColor = '#4a90e2';
    });
    uploadBtn.addEventListener('mouseleave', () => {
      uploadBtn.style.background = '#1a2030';
      uploadBtn.style.borderColor = '#3a5078';
    });
    uploadBtn.addEventListener('click', () => { void this.openUploadDialog(listPanel); });
    listPanel.appendChild(uploadBtn);

    if (this.scenarios.length === 0) {
      const empty = document.createElement('div');
      empty.textContent = t('mapSelect.emptyCatalog');
      Object.assign(empty.style, {
        padding: '12px 14px', fontSize: '12px', opacity: '0.5', fontStyle: 'italic',
      });
      listPanel.appendChild(empty);
    }
    for (const sc of this.scenarios) {
      const item = document.createElement('div');
      Object.assign(item.style, {
        padding: '8px 14px', marginBottom: '2px', borderRadius: '6px', cursor: 'pointer',
        border: '1px solid transparent', transition: 'background 0.15s',
        fontSize: '12px', fontWeight: '500',
      });
      const baseName = scenarioDisplayName(sc);
      const prefix = this.uploadedScenarios.has(sc) ? '📁 ' : '';
      // Map size + player count live here on the list (not beside the description).
      item.innerHTML = `
        <div style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${prefix}${baseName}</div>
        <div style="font-size:10px;font-weight:400;opacity:0.5;margin-top:2px">${sc.width}×${sc.height} · ${t('mapSelect.playersCount', sc.players.length)}</div>
      `;
      item.addEventListener('mouseenter', () => {
        if (sc !== this.selected) item.style.background = '#1a1f2a';
      });
      item.addEventListener('mouseleave', () => {
        if (sc !== this.selected) item.style.background = 'transparent';
      });
      item.addEventListener('click', () => {
        audio.sfx.play('ui-click');
        this.selected = sc;
        this.initSelectionsFor(sc);
        this.updatePreview();
        this.highlightListItem(listPanel, item);
      });
      if (sc === this.selected) {
        item.style.background = '#253050';
        item.style.borderColor = '#4a90e2';
      }
      listPanel.appendChild(item);
    }
    body.appendChild(listPanel);

    // ── Content area ──
    const content = document.createElement('div');
    Object.assign(content.style, {
      flex: '1', display: 'flex', flexDirection: 'column',
      overflow: 'hidden', padding: '8px 16px',
      minHeight: '0',
    });

    // Top row: map + human card side by side. It GROWS to fill the height left
    // over by the CPU grid (so there's no dead space at the bottom), with a
    // minimum so it never collapses on short screens. Both children stretch to
    // its full height, giving the human card room for the whole bio without an
    // inner scrollbar and the description plenty of space.
    const topRow = document.createElement('div');
    Object.assign(topRow.style, {
      display: 'flex', gap: '12px',
      flex: '1 1 0', minHeight: `${MapSelectScene.TOP_ROW_HEIGHT}px`,
      maxHeight: `${MapSelectScene.TOP_ROW_MAX_HEIGHT}px`, overflow: 'hidden',
    });

    // Map column — equal width to the human card (both flex:1). It holds one
    // scenario panel where the minimap floats left and the title + description
    // wrap around it.
    const mapCol = document.createElement('div');
    Object.assign(mapCol.style, { flex: '1', minWidth: '0', display: 'flex', flexDirection: 'column' });
    this.scenarioInfoContainer = document.createElement('div');
    // Fills the column height; overflow hidden so a very long description clips
    // rather than reflowing the layout.
    Object.assign(this.scenarioInfoContainer.style, {
      width: '100%', flex: '1', minHeight: '0', overflow: 'hidden',
    });
    mapCol.appendChild(this.scenarioInfoContainer);
    topRow.appendChild(mapCol);

    // Human player card placeholder — flex so the card stretches to full height.
    this.humanCardContainer = document.createElement('div');
    Object.assign(this.humanCardContainer.style, { flex: '1', minWidth: '0', display: 'flex' });
    topRow.appendChild(this.humanCardContainer);

    content.appendChild(topRow);

    // CPU cards grid (max 4 per row)
    this.cpuCardsContainer = document.createElement('div');
    Object.assign(this.cpuCardsContainer.style, {
      marginTop: '8px',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      gap: '8px',
      overflow: 'auto', minHeight: '0',
    });
    content.appendChild(this.cpuCardsContainer);

    body.appendChild(content);
    root.appendChild(body);

    // Footer
    const footer = document.createElement('div');
    Object.assign(footer.style, {
      display: 'flex', justifyContent: 'flex-end', gap: '10px',
      padding: '14px 20px', borderTop: '1px solid #2a3140',
    });
    this.startBtn = document.createElement('button');
    const startBtn = this.startBtn;
    startBtn.className = 'img-btn';
    startBtn.textContent = t('mapSelect.startGame');
    startBtn.style.cssText = this.imgBtnCss('/graphics/ui/confirmation-button-background.png', 180);
    startBtn.addEventListener('click', () => {
      if (startBtn.disabled) return;
      audio.sfx.play('ui-click');
      this.startGame();
    });
    const backBtn = document.createElement('button');
    backBtn.className = 'img-btn';
    backBtn.textContent = t('mapSelect.back');
    backBtn.style.cssText = this.imgBtnCss('/graphics/ui/cancel-button-background.png', 150);
    backBtn.addEventListener('click', () => {
      audio.sfx.play('ui-click');
      this.scene.start('GameModeSelect');
    });
    footer.appendChild(startBtn);
    footer.appendChild(backBtn);
    root.appendChild(footer);

    return root;
  }

  private async openUploadDialog(listPanel: HTMLElement): Promise<void> {
    audio.sfx.play('ui-click');
    const file = await new Promise<File | null>((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.json,application/json';
      input.addEventListener('change', () => resolve(input.files?.[0] ?? null));
      input.addEventListener('cancel', () => resolve(null));
      input.click();
    });
    if (!file) return;

    let raw: unknown;
    try {
      raw = JSON.parse(await file.text());
    } catch (e) {
      this.showUploadError([`Plik nie jest poprawnym JSON-em: ${(e as Error).message}`]);
      return;
    }
    const result = validateScenario(raw);
    if (!result.ok) {
      this.showUploadError(result.errors);
      return;
    }

    // Add to the top of the list and select it.
    this.scenarios = [result.scenario, ...this.scenarios];
    this.uploadedScenarios.add(result.scenario);
    this.selected = result.scenario;
    this.initSelectionsFor(result.scenario);

    // Rebuild the DOM so the new entry appears.
    this.dom?.remove();
    this.dom = this.buildDom();
    document.body.appendChild(this.dom);
    this.updatePreview();
    void listPanel;
  }

  private showUploadError(errors: string[]): void {
    const overlay = document.createElement('div');
    Object.assign(overlay.style, {
      position: 'fixed', inset: '0', background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: '500',
    });
    const modal = document.createElement('div');
    Object.assign(modal.style, {
      background: '#22262e', border: '1px solid #3a4150',
      borderRadius: '8px', minWidth: '420px', maxWidth: '600px',
      maxHeight: '70vh', display: 'flex', flexDirection: 'column',
      boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
    });
    const title = document.createElement('div');
    title.textContent = 'Niepoprawny plik scenariusza';
    Object.assign(title.style, {
      padding: '12px 16px', borderBottom: '1px solid #3a4150',
      fontSize: '14px', fontWeight: '700', color: '#d8dee9',
    });
    modal.appendChild(title);
    const body = document.createElement('div');
    Object.assign(body.style, { padding: '16px', overflowY: 'auto' });
    const intro = document.createElement('div');
    intro.textContent = 'Plik nie przeszedł walidacji. Lista pozostała bez zmian.';
    Object.assign(intro.style, { color: '#d8dee9', marginBottom: '8px', fontSize: '13px' });
    body.appendChild(intro);
    const list = document.createElement('ul');
    Object.assign(list.style, { paddingLeft: '20px', fontSize: '12px', color: '#d8dee9', opacity: '0.85' });
    for (const err of errors) {
      const li = document.createElement('li');
      li.textContent = err;
      Object.assign(li.style, { marginBottom: '4px' });
      list.appendChild(li);
    }
    body.appendChild(list);
    modal.appendChild(body);
    const footer = document.createElement('div');
    Object.assign(footer.style, {
      display: 'flex', justifyContent: 'flex-end',
      padding: '12px 16px', borderTop: '1px solid #3a4150',
    });
    const ok = document.createElement('button');
    ok.textContent = 'OK';
    Object.assign(ok.style, {
      padding: '8px 16px', background: '#4a90e2', color: '#fff',
      border: '1px solid #4a90e2', borderRadius: '4px', cursor: 'pointer',
      fontFamily: 'inherit', fontSize: '13px', fontWeight: '600',
    });
    ok.addEventListener('click', () => overlay.remove());
    footer.appendChild(ok);
    modal.appendChild(footer);
    overlay.appendChild(modal);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
    document.body.appendChild(overlay);
  }

  private highlightListItem(listPanel: HTMLElement, active: HTMLElement): void {
    for (const child of listPanel.children) {
      const el = child as HTMLElement;
      el.style.background = 'transparent';
      el.style.borderColor = 'transparent';
    }
    active.style.background = '#253050';
    active.style.borderColor = '#4a90e2';
  }

  // ─── Preview / layout update ──────────────────────────────────

  private updatePreview(): void {
    const sc = this.selected;
    if (this.startBtn) this.startBtn.disabled = sc == null;
    if (!sc) {
      if (this.scenarioInfoContainer) this.scenarioInfoContainer.innerHTML = '';
      if (this.humanCardContainer) this.humanCardContainer.innerHTML = '';
      if (this.cpuCardsContainer) this.cpuCardsContainer.innerHTML = '';
      return;
    }

    if (this.scenarioInfoContainer) {
      const panel = this.scenarioInfoContainer;
      panel.innerHTML = '';

      // Minimap floats left so the title + description text wraps around it.
      const canvas = renderMinimapCanvas(sc, MapSelectScene.MINIMAP_SIZE);
      Object.assign(canvas.style, {
        width: `${MapSelectScene.MINIMAP_SIZE}px`, height: `${MapSelectScene.MINIMAP_SIZE}px`,
        float: 'left', margin: '2px 14px 8px 0', borderRadius: '4px',
      });
      panel.appendChild(canvas);

      const title = document.createElement('div');
      title.textContent = scenarioDisplayName(sc);
      Object.assign(title.style, {
        fontSize: '20px', fontWeight: '700', marginBottom: '8px',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      });
      panel.appendChild(title);

      const desc = scenarioDescription(sc);
      if (desc) {
        const d = document.createElement('div');
        d.textContent = desc;
        Object.assign(d.style, { fontSize: '13px', lineHeight: '1.55', opacity: '0.8' });
        panel.appendChild(d);
      }
    }

    this.cardContentRefs = [];
    this.pennantRefs = [];

    if (this.humanCardContainer) {
      this.humanCardContainer.innerHTML = '';
      this.humanCardContainer.appendChild(this.buildPlayerCard(0, true));
    }

    if (this.cpuCardsContainer) {
      this.cpuCardsContainer.innerHTML = '';
      for (let slot = 1; slot < sc.players.length; slot++) {
        this.cpuCardsContainer.appendChild(this.buildPlayerCard(slot, false));
      }
    }

    this.syncPennants();
  }

  // ─── Player card ──────────────────────────────────────────────

  private buildPlayerCard(slot: number, isHuman: boolean): HTMLElement {
    const card = document.createElement('div');
    Object.assign(card.style, {
      background: 'rgba(18, 21, 26, 0.85)',
      border: '1px solid #2a3140', borderRadius: '8px',
      padding: isHuman ? '10px 12px' : '8px',
      display: 'flex', flexDirection: 'column',
      // Human card fills the (growing) top row; CPU cards keep a fixed height.
      // Either way the height is independent of the selection, so picking a
      // character never resizes the card.
      ...(isHuman ? { flex: '1', height: '100%' } : { height: `${MapSelectScene.CPU_CARD_HEIGHT}px` }),
      boxSizing: 'border-box',
    });

    if (isHuman) {
      this.humanCard = card;
      this.updateHumanCardGlow();
    }

    // Clickable content area (opens carousel)
    const contentArea = document.createElement('div');
    Object.assign(contentArea.style, {
      cursor: 'pointer', transition: 'opacity 0.15s', flex: '1', minHeight: '0',
      // Human card grows tall, so a long bio scrolls within it (never cut, never
      // resizes the card). CPU cards are compact with short content — just clip,
      // so their fixed height never sprouts a scrollbar.
      overflowY: isHuman ? 'auto' : 'hidden',
    });
    contentArea.addEventListener('mouseenter', () => {
      if (!isHuman || this.selections[0] !== 'random') {
        card.style.borderColor = '#4a6080';
        card.style.boxShadow = '0 0 12px rgba(74, 144, 226, 0.15)';
      }
    });
    contentArea.addEventListener('mouseleave', () => {
      if (!isHuman || this.selections[0] !== 'random') {
        card.style.borderColor = '#2a3140';
        card.style.boxShadow = 'none';
      } else {
        this.updateHumanCardGlow();
      }
    });
    contentArea.addEventListener('click', () => this.openCarousel(slot));
    // RMB context menu — bound on the whole card (not just contentArea) so
    // the quick-pick popup opens regardless of which part of the card the
    // player right-clicked.
    card.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      this.openQuickPickMenu(slot, e.clientX, e.clientY);
    });

    this.cardContentRefs[slot] = contentArea;
    this.renderCardContent(slot, isHuman);
    card.appendChild(contentArea);

    // Input hint — explain which mouse button does what.
    const hint = document.createElement('div');
    hint.textContent = t('mapSelect.pickHint');
    Object.assign(hint.style, {
      fontSize: '10px', opacity: '0.45', textAlign: 'center',
      padding: '2px 4px 4px', lineHeight: '1.3',
      pointerEvents: 'none', userSelect: 'none', flexShrink: '0',
    } as Partial<CSSStyleDeclaration>);
    card.appendChild(hint);

    // Pennant row — pinned at the bottom of the fixed-height card.
    const pennantRow = this.buildPennantRow(slot, isHuman);
    pennantRow.style.flexShrink = '0';
    card.appendChild(pennantRow);

    return card;
  }

  private renderCardContent(slot: number, isHuman: boolean): void {
    const container = this.cardContentRefs[slot];
    if (!container) return;
    container.innerHTML = '';

    const sel = this.selections[slot];

    if (!isHuman) {
      const label = document.createElement('div');
      label.textContent = t('mapSelect.computer', slot);
      Object.assign(label.style, {
        fontSize: '12px', fontWeight: '600', opacity: '0.4',
        textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px',
      });
      container.appendChild(label);
    }

    if (sel === 'random') {
      this.renderRandomCardContent(container, isHuman);
    } else {
      this.renderCharacterCardContent(container, sel, isHuman);
    }
  }

  private renderRandomCardContent(container: HTMLDivElement, isHuman: boolean): void {
    const row = document.createElement('div');
    Object.assign(row.style, {
      display: 'flex', gap: isHuman ? '14px' : '10px', alignItems: 'center',
    });

    // Same footprint as a real portrait box (see renderCharacterCardContent),
    // so swapping between "random" and a picked character doesn't resize the card.
    const phSize = isHuman ? { w: 78, h: 110 } : { w: 44, h: 62 };
    const ph = document.createElement('div');
    Object.assign(ph.style, {
      width: `${phSize.w}px`, height: `${phSize.h}px`,
      background: '#1a1f2a', border: '1px solid #2a3140', borderRadius: '4px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: isHuman ? '36px' : '26px', color: '#555', flexShrink: '0',
    });
    ph.textContent = '?';
    row.appendChild(ph);

    const textCol = document.createElement('div');
    const titleEl = document.createElement('div');
    titleEl.textContent = t('mapSelect.randomCharacterLabel');
    Object.assign(titleEl.style, {
      fontSize: isHuman ? '17px' : '14px', fontWeight: '700', color: '#e5e9f0',
    });
    textCol.appendChild(titleEl);

    const subtitleEl = document.createElement('div');
    subtitleEl.textContent = t('mapSelect.randomFactionLabel');
    Object.assign(subtitleEl.style, {
      fontSize: isHuman ? '14px' : '12px', opacity: '0.5', marginTop: '2px',
    });
    textCol.appendChild(subtitleEl);

    const hint = document.createElement('div');
    hint.textContent = t('mapSelect.clickToSelect');
    Object.assign(hint.style, {
      fontSize: isHuman ? '13px' : '11px', opacity: '0.35', fontStyle: 'italic',
      marginTop: isHuman ? '10px' : '6px',
    });
    textCol.appendChild(hint);

    row.appendChild(textCol);
    container.appendChild(row);
  }

  private renderCharacterCardContent(
    container: HTMLDivElement, sel: string, isHuman: boolean,
  ): void {
    const colonIdx = sel.indexOf(':');
    if (colonIdx < 0) return;
    const factionId = sel.slice(0, colonIdx);
    const charId = sel.slice(colonIdx + 1);

    const faction = FACTIONS.find((f) => f.id === factionId);
    if (!faction) return;
    const character = faction.characters.find((c) => c.id === charId);
    if (!character) return;

    const row = document.createElement('div');
    Object.assign(row.style, { display: 'flex', gap: isHuman ? '14px' : '10px' });

    // Portrait
    const portraitSize = isHuman ? { w: 78, h: 110 } : { w: 44, h: 62 };
    const portraitBox = document.createElement('div');
    Object.assign(portraitBox.style, {
      width: `${portraitSize.w}px`, height: `${portraitSize.h}px`,
      borderRadius: '4px', overflow: 'hidden', flexShrink: '0',
      border: '1px solid #2a3140', background: '#1a1f2a',
    });
    if (character.portrait) {
      const img = document.createElement('img');
      img.src = character.portrait;
      Object.assign(img.style, {
        width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top',
      });
      portraitBox.appendChild(img);
    }
    row.appendChild(portraitBox);

    // Text info column
    const textCol = document.createElement('div');
    Object.assign(textCol.style, {
      flex: '1', minWidth: '0',
      ...(!isHuman ? { display: 'flex', flexDirection: 'column', justifyContent: 'space-between' } : {}),
    });

    if (!isHuman) {
      // CPU: name + faction on top, flag bottom-right
      const topPart = document.createElement('div');

      const charNameEl = document.createElement('div');
      charNameEl.textContent = t(`faction.${factionId}.${charId}.name`);
      Object.assign(charNameEl.style, { fontSize: '14px', fontWeight: '700', color: '#e5e9f0' });
      topPart.appendChild(charNameEl);

      const fNameEl = document.createElement('div');
      fNameEl.textContent = t(`faction.${factionId}`);
      Object.assign(fNameEl.style, { fontSize: '12px', fontWeight: '600', opacity: '0.6' });
      topPart.appendChild(fNameEl);

      textCol.appendChild(topPart);

      const flagRow = document.createElement('div');
      Object.assign(flagRow.style, { display: 'flex', justifyContent: 'flex-end' });
      const flagEl = document.createElement('div');
      Object.assign(flagEl.style, {
        width: '48px', height: '28px', borderRadius: '3px',
        overflow: 'hidden', border: '1px solid #2a3140',
      });
      applyFlagStyle(flagEl, factionId, 48, 28);
      flagRow.appendChild(flagEl);
      textCol.appendChild(flagRow);
    }

    if (isHuman) {
      // Human: flag floated right, full details
      const flagSize = { w: 90, h: 53 };
      const flagWrap = document.createElement('div');
      Object.assign(flagWrap.style, { float: 'right', margin: '0 0 6px 10px' });
      const flagEl = document.createElement('div');
      Object.assign(flagEl.style, {
        width: `${flagSize.w}px`, height: `${flagSize.h}px`,
        borderRadius: '3px', overflow: 'hidden', border: '1px solid #2a3140',
      });
      applyFlagStyle(flagEl, factionId, flagSize.w, flagSize.h);
      flagWrap.appendChild(flagEl);
      textCol.appendChild(flagWrap);

      const charNameEl = document.createElement('div');
      charNameEl.textContent = t(`faction.${factionId}.${charId}.name`);
      Object.assign(charNameEl.style, { fontSize: '20px', fontWeight: '700', color: '#e5e9f0' });
      textCol.appendChild(charNameEl);

      const fNameEl = document.createElement('div');
      fNameEl.textContent = t(`faction.${factionId}`);
      Object.assign(fNameEl.style, { fontSize: '16px', fontWeight: '600', opacity: '0.6', marginBottom: '3px' });
      textCol.appendChild(fNameEl);
    }

    if (isHuman) {
      // Faction description (flows around flag)
      const descKey = `faction.${factionId}.desc`;
      const descText = t(descKey);
      if (descText !== descKey) {
        const fDescEl = document.createElement('div');
        fDescEl.textContent = descText;
        Object.assign(fDescEl.style, {
          fontSize: '14px', lineHeight: '1.4', opacity: '0.5', fontStyle: 'italic',
        });
        textCol.appendChild(fDescEl);
      }

      // Clear float
      const clearEl = document.createElement('div');
      clearEl.style.clear = 'both';
      textCol.appendChild(clearEl);

      // Unique units — kept ABOVE the bio so the gameplay-relevant info is
      // visible first (the longer flavour bio follows below).
      const uniqueUnits = FACTION_UNIQUE_UNITS[factionId];
      if (uniqueUnits && uniqueUnits.length > 0) {
        const unitsRow = document.createElement('div');
        Object.assign(unitsRow.style, {
          display: 'flex', gap: '10px', marginTop: '6px', flexWrap: 'wrap',
        });
        for (const u of uniqueUnits) {
          const unitEl = document.createElement('div');
          Object.assign(unitEl.style, { display: 'flex', alignItems: 'center', gap: '4px' });
          const iconPath = UNIT_ICON_PATHS[u.unitType];
          if (iconPath) {
            const icon = document.createElement('img');
            icon.src = iconPath;
            Object.assign(icon.style, {
              maxWidth: '56px', maxHeight: '56px', width: 'auto', height: 'auto',
              objectFit: 'contain',
            });
            unitEl.appendChild(icon);
          }
          const nameEl = document.createElement('span');
          nameEl.textContent = t(`unit.${u.unitType}`);
          Object.assign(nameEl.style, { fontSize: '12px', fontWeight: '600' });
          unitEl.appendChild(nameEl);
          unitsRow.appendChild(unitEl);
        }
        textCol.appendChild(unitsRow);
      }

      // Character bio (flavour) — last; the content area scrolls if it and the
      // above don't all fit, so nothing is hard-cut.
      const bioEl = document.createElement('div');
      bioEl.textContent = t(`faction.${factionId}.${charId}.bio`);
      Object.assign(bioEl.style, {
        fontSize: '14px', lineHeight: '1.4', opacity: '0.6', marginTop: '6px',
      });
      textCol.appendChild(bioEl);
    }

    row.appendChild(textCol);
    container.appendChild(row);
  }

  // ─── Pennants (color selection) ───────────────────────────────

  private buildPennantRow(slot: number, isHuman: boolean): HTMLElement {
    const row = document.createElement('div');
    Object.assign(row.style, {
      display: 'flex', gap: isHuman ? '6px' : '5px',
      marginTop: isHuman ? '10px' : '8px',
      paddingTop: isHuman ? '8px' : '6px',
      borderTop: '1px solid rgba(42, 49, 64, 0.4)',
    });

    const pCount = this.selected?.players.length ?? 0;
    row.appendChild(this.buildPennant(slot, -1, isHuman));
    for (let i = 0; i < pCount; i++) {
      row.appendChild(this.buildPennant(slot, i, isHuman));
    }
    return row;
  }

  private buildPennant(slot: number, colorIndex: number, isHuman: boolean): HTMLElement {
    const pw = isHuman ? 22 : 18;
    const ph = isHuman ? 30 : 24;

    const wrapper = document.createElement('div');
    Object.assign(wrapper.style, {
      transition: 'transform 0.15s, filter 0.15s',
      cursor: 'pointer',
    });

    const pennant = document.createElement('div');
    const bgColor = colorIndex >= 0 ? PLAYER_COLORS[colorIndex] : '#555';
    Object.assign(pennant.style, {
      width: `${pw}px`, height: `${ph}px`,
      background: bgColor,
      clipPath: 'polygon(0 0, 100% 0, 100% 70%, 50% 100%, 0 70%)',
    });

    if (colorIndex < 0) {
      Object.assign(pennant.style, {
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: '1px', fontSize: isHuman ? '15px' : '13px',
        fontWeight: '700', color: '#fff',
      });
      pennant.textContent = '?';
    }

    wrapper.appendChild(pennant);
    this.pennantRefs.push({ el: pennant, wrapper, slot, colorIndex });

    wrapper.addEventListener('click', (e) => {
      e.stopPropagation();
      if (colorIndex >= 0) {
        const owner = this.colorSelections.findIndex((c, s) => c === colorIndex && s !== slot);
        if (owner >= 0) return;
      }
      audio.sfx.play('ui-click');
      this.colorSelections[slot] = colorIndex;
      this.syncPennants();
      // In forced-faction scenarios the locked faction follows the chosen
      // side: re-pin selections to the new player and re-render the cards.
      if (this.selected?.factions) {
        this.syncForcedFactionSelections();
        for (let s = 0; s < this.selections.length; s++) {
          this.renderCardContent(s, s === 0);
        }
      }
    });

    wrapper.addEventListener('mouseenter', () => {
      if (colorIndex >= 0) {
        const owner = this.colorSelections.findIndex((c, s) => c === colorIndex && s !== slot);
        if (owner >= 0) return;
      }
      if (this.colorSelections[slot] !== colorIndex) {
        wrapper.style.transform = 'scale(1.2)';
      }
    });
    wrapper.addEventListener('mouseleave', () => {
      this.syncPennants();
    });

    return wrapper;
  }

  private syncPennants(): void {
    const taken = new Map<number, number>();
    for (let s = 0; s < this.colorSelections.length; s++) {
      if (this.colorSelections[s] >= 0) taken.set(this.colorSelections[s], s);
    }

    for (const p of this.pennantRefs) {
      const isSelected = this.colorSelections[p.slot] === p.colorIndex;
      const owner = taken.get(p.colorIndex);
      const isTaken = p.colorIndex >= 0 && owner !== undefined && owner !== p.slot;

      if (isTaken) {
        p.wrapper.style.opacity = '0.2';
        p.wrapper.style.cursor = 'default';
        p.wrapper.style.transform = 'scale(1)';
        p.wrapper.style.filter = 'none';
      } else if (isSelected) {
        p.wrapper.style.opacity = '1';
        p.wrapper.style.cursor = 'pointer';
        p.wrapper.style.transform = 'scale(1.15)';
        const glowColor = p.colorIndex >= 0 ? PLAYER_COLORS[p.colorIndex] : '#aaa';
        p.wrapper.style.filter = `drop-shadow(0 0 3px ${glowColor}) drop-shadow(0 0 6px ${glowColor})`;
      } else {
        p.wrapper.style.opacity = '0.65';
        p.wrapper.style.cursor = 'pointer';
        p.wrapper.style.transform = 'scale(1)';
        p.wrapper.style.filter = 'none';
      }
    }
  }

  // ─── Carousel modal ──────────────────────────────────────────

  private openCarousel(slot: number): void {
    // Scenario-forced factions are read-only: the picker doesn't open.
    if (this.forcedFactionForSlot(slot)) return;
    audio.sfx.play('ui-click');
    this.carouselSlot = slot;
    this.carouselAnimating = false;

    const currentSel = this.selections[slot];
    this.carouselIndex = Math.max(0, CAROUSEL_ITEMS.findIndex((item) => item.key === currentSel));

    // Overlay
    this.carouselOverlay = document.createElement('div');
    Object.assign(this.carouselOverlay.style, {
      position: 'fixed', inset: '0',
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: '200',
    });
    // Clicking outside the modal does NOT close it — only X or confirm button

    // Modal — large enough to avoid scrollbar
    const modal = document.createElement('div');
    Object.assign(modal.style, {
      width: '860px', height: '570px',
      background: '#12151a', border: '1px solid #3a4560', borderRadius: '12px',
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
      boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
    });
    modal.addEventListener('click', (e) => e.stopPropagation());

    // Title bar
    const titleBar = document.createElement('div');
    Object.assign(titleBar.style, {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '8px 16px', borderBottom: '1px solid #2a3140', flexShrink: '0',
    });
    const titleText = document.createElement('span');
    titleText.textContent = t('mapSelect.selectCharacter');
    Object.assign(titleText.style, {
      fontSize: '14px', fontWeight: '700', letterSpacing: '1.5px', color: '#d8dee9',
    });
    titleBar.appendChild(titleText);

    const closeBtn = document.createElement('button');
    closeBtn.textContent = '\u00d7';
    Object.assign(closeBtn.style, {
      background: 'none', border: 'none', color: '#888',
      fontSize: '22px', cursor: 'pointer', padding: '0 4px', fontFamily: 'inherit',
    });
    closeBtn.addEventListener('mouseenter', () => { closeBtn.style.color = '#ddd'; });
    closeBtn.addEventListener('mouseleave', () => { closeBtn.style.color = '#888'; });
    closeBtn.addEventListener('click', () => this.closeCarousel());
    titleBar.appendChild(closeBtn);
    modal.appendChild(titleBar);

    // Body: [arrow] [card: portrait | info] [arrow]
    const body = document.createElement('div');
    Object.assign(body.style, {
      flex: '1', padding: '10px 12px', overflow: 'hidden',
      display: 'flex', alignItems: 'stretch', gap: '10px',
    });

    // Left arrow
    body.appendChild(this.buildCarouselArrow('\u25c0', () => this.navigateCarousel(-1)));

    // Card area (portrait left + info right)
    this.carouselCardBox = document.createElement('div');
    Object.assign(this.carouselCardBox.style, {
      flex: '1', display: 'flex', gap: '14px', minWidth: '0', overflow: 'hidden',
      transition: 'opacity 0.15s ease, transform 0.15s ease',
    });
    body.appendChild(this.carouselCardBox);

    // Right arrow
    body.appendChild(this.buildCarouselArrow('\u25b6', () => this.navigateCarousel(1)));

    modal.appendChild(body);

    // Footer: counter + confirm button
    const actions = document.createElement('div');
    Object.assign(actions.style, {
      padding: '8px 16px', borderTop: '1px solid #2a3140',
      display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px',
      flexShrink: '0',
    });

    const confirmBtn = document.createElement('button');
    confirmBtn.className = 'img-btn';
    confirmBtn.textContent = t('mapSelect.confirm');
    confirmBtn.style.cssText = this.imgBtnCss('/graphics/ui/confirmation-button-background.png', 180);
    confirmBtn.addEventListener('click', () => {
      audio.sfx.play('ui-click');
      const item = CAROUSEL_ITEMS[this.carouselIndex];
      this.selections[this.carouselSlot] = item.key;
      const isHuman = this.carouselSlot === 0;
      this.renderCardContent(this.carouselSlot, isHuman);
      this.updateHumanCardGlow();
      this.closeCarousel();
    });
    actions.appendChild(confirmBtn);
    modal.appendChild(actions);

    this.carouselOverlay.appendChild(modal);
    document.body.appendChild(this.carouselOverlay);

    // Keyboard navigation
    this.carouselKeyHandler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        this.navigateCarousel(-1);
      } else if (e.key === 'ArrowRight') {
        this.navigateCarousel(1);
      } else if (e.key === 'Enter') {
        confirmBtn.click();
      } else if (e.key === 'Escape') {
        this.closeCarousel();
      }
    };
    document.addEventListener('keydown', this.carouselKeyHandler);

    this.updateCarouselDisplay();
  }

  private buildCarouselArrow(symbol: string, onClick: () => void): HTMLElement {
    const btn = document.createElement('button');
    btn.textContent = symbol;
    Object.assign(btn.style, {
      width: '44px', minWidth: '44px', borderRadius: '8px',
      background: 'rgba(255,255,255,0.06)', border: '1px solid #2a3140',
      color: '#d8dee9', fontSize: '18px', cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'inherit', transition: 'background 0.15s',
      flexShrink: '0', alignSelf: 'center', height: '60px',
    });
    btn.addEventListener('mouseenter', () => { btn.style.background = 'rgba(255,255,255,0.12)'; });
    btn.addEventListener('mouseleave', () => { btn.style.background = 'rgba(255,255,255,0.06)'; });
    btn.addEventListener('click', () => {
      audio.sfx.play('ui-click');
      onClick();
    });
    return btn;
  }

  private navigateCarousel(dir: -1 | 1): void {
    if (this.carouselAnimating || !this.carouselCardBox) return;
    this.carouselAnimating = true;

    const box = this.carouselCardBox;
    const shift = dir === -1 ? '30px' : '-30px';

    // Slide out
    box.style.opacity = '0';
    box.style.transform = `translateX(${shift})`;

    setTimeout(() => {
      this.carouselIndex = (this.carouselIndex + dir + CAROUSEL_ITEMS.length) % CAROUSEL_ITEMS.length;
      this.updateCarouselDisplay();

      // Position for slide in from opposite side
      box.style.transition = 'none';
      box.style.transform = `translateX(${dir === -1 ? '-30px' : '30px'})`;

      // Force reflow, then animate in
      void box.offsetWidth;
      box.style.transition = 'opacity 0.15s ease, transform 0.15s ease';
      box.style.opacity = '1';
      box.style.transform = 'translateX(0)';

      setTimeout(() => { this.carouselAnimating = false; }, 150);
    }, 150);
  }

  /** Render the current carousel card (portrait + info). */
  private updateCarouselDisplay(): void {
    if (!this.carouselCardBox) return;
    this.carouselCardBox.innerHTML = '';

    const item = CAROUSEL_ITEMS[this.carouselIndex];


    if (!item.factionId || !item.charId) {
      // Random card
      this.renderCarouselRandom();
      return;
    }

    const fid = item.factionId;
    const cid = item.charId;
    const faction = FACTIONS.find((f) => f.id === fid);
    if (!faction) return;
    const character = faction.characters.find((c) => c.id === cid);
    if (!character) return;

    // Portrait (left) — bust framed at the 4:5 ratio of /portraits/<key>.png and
    // vertically centred (the card is far taller than a bust, so don't stretch).
    const portraitBox = document.createElement('div');
    Object.assign(portraitBox.style, {
      width: '180px', height: '225px', alignSelf: 'center', flexShrink: '0',
      borderRadius: '6px', overflow: 'hidden', border: '1px solid #2a3140', background: '#1a1f2a',
    });
    if (character.portrait) {
      const img = document.createElement('img');
      img.src = character.portrait;
      Object.assign(img.style, {
        width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top',
      });
      portraitBox.appendChild(img);
    }
    this.carouselCardBox.appendChild(portraitBox);

    // Info (right) — two sections: description (scrollable) + units (bottom)
    const infoBox = document.createElement('div');
    Object.assign(infoBox.style, {
      flex: '1', minWidth: '0', display: 'flex', flexDirection: 'column',
    });

    // ── Description section (flex:1, scrollable) ──
    const descSection = document.createElement('div');
    Object.assign(descSection.style, {
      flex: '1', overflowY: 'auto', minHeight: '0',
    });

    // Flag floated right
    const flagWrap = document.createElement('div');
    Object.assign(flagWrap.style, { float: 'right', margin: '0 0 8px 12px' });
    const flagEl = document.createElement('div');
    Object.assign(flagEl.style, {
      width: '100px', height: '59px', borderRadius: '4px',
      overflow: 'hidden', border: '1px solid #2a3140',
    });
    applyFlagStyle(flagEl, fid, 100, 59);
    flagWrap.appendChild(flagEl);
    descSection.appendChild(flagWrap);

    // Character name
    const nameEl = document.createElement('div');
    nameEl.textContent = t(`faction.${fid}.${cid}.name`);
    Object.assign(nameEl.style, { fontSize: '26px', fontWeight: '700', color: '#e5e9f0' });
    descSection.appendChild(nameEl);

    // Faction name
    const fNameEl = document.createElement('div');
    fNameEl.textContent = t(`faction.${fid}`);
    Object.assign(fNameEl.style, {
      fontSize: '16px', fontWeight: '600', opacity: '0.55', marginBottom: '6px',
    });
    descSection.appendChild(fNameEl);

    // Faction description (flows around flag)
    const descKey = `faction.${fid}.desc`;
    const descText = t(descKey);
    if (descText !== descKey) {
      const fDescEl = document.createElement('div');
      fDescEl.textContent = descText;
      Object.assign(fDescEl.style, {
        fontSize: '15px', lineHeight: '1.45', opacity: '0.55', fontStyle: 'italic',
        marginBottom: '6px',
      });
      descSection.appendChild(fDescEl);
    }

    // Clear float
    const clearEl = document.createElement('div');
    clearEl.style.clear = 'both';
    descSection.appendChild(clearEl);

    // Character bio
    const bioEl = document.createElement('div');
    bioEl.textContent = t(`faction.${fid}.${cid}.bio`);
    Object.assign(bioEl.style, {
      fontSize: '15px', lineHeight: '1.45', opacity: '0.65',
    });
    descSection.appendChild(bioEl);

    infoBox.appendChild(descSection);

    // ── Unique units section (fixed at bottom) ──
    const uniqueUnits = FACTION_UNIQUE_UNITS[fid];
    if (uniqueUnits && uniqueUnits.length > 0) {
      const unitsSection = document.createElement('div');
      Object.assign(unitsSection.style, {
        flexShrink: '0', borderTop: '1px solid #2a3140',
        paddingTop: '8px', marginTop: '8px',
      });

      const unitsLabel = document.createElement('div');
      unitsLabel.textContent = t('mapSelect.uniqueUnits');
      Object.assign(unitsLabel.style, {
        fontSize: '11px', fontWeight: '600', opacity: '0.4',
        textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px',
      });
      unitsSection.appendChild(unitsLabel);

      for (const u of uniqueUnits) {
        const unitBlock = document.createElement('div');
        Object.assign(unitBlock.style, { marginBottom: '6px' });

        // Icon floated left, description flows around it
        const iconPath = UNIT_ICON_PATHS[u.unitType];
        if (iconPath) {
          const iconWrap = document.createElement('div');
          Object.assign(iconWrap.style, {
            float: 'left', margin: '0 10px 4px 0',
            width: '64px', height: '64px', borderRadius: '6px',
            background: '#ffffff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          });
          const icon = document.createElement('img');
          icon.src = iconPath;
          Object.assign(icon.style, {
            maxWidth: '52px', maxHeight: '52px', width: 'auto', height: 'auto',
            objectFit: 'contain',
          });
          iconWrap.appendChild(icon);
          unitBlock.appendChild(iconWrap);
        }
        const uDescEl = document.createElement('div');
        uDescEl.textContent = t(`unit.${u.unitType}.desc`);
        Object.assign(uDescEl.style, {
          fontSize: '14px', lineHeight: '1.4', opacity: '0.6',
        });
        unitBlock.appendChild(uDescEl);

        // Clear float
        const unitClear = document.createElement('div');
        unitClear.style.clear = 'both';
        unitBlock.appendChild(unitClear);

        unitsSection.appendChild(unitBlock);
      }
      infoBox.appendChild(unitsSection);
    }

    this.carouselCardBox.appendChild(infoBox);
  }

  private renderCarouselRandom(): void {
    if (!this.carouselCardBox) return;

    // "?" placeholder (left) — exact same footprint as a real portrait box
    // (see renderCarouselCharacter), so the card doesn't jump between random
    // and a picked character.
    const phBox = document.createElement('div');
    Object.assign(phBox.style, {
      width: '180px', height: '225px', alignSelf: 'center', flexShrink: '0',
      borderRadius: '6px',
      overflow: 'hidden', border: '1px solid #2a3140', background: '#1a1f2a',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    });
    const q = document.createElement('span');
    q.textContent = '?';
    Object.assign(q.style, { fontSize: '72px', color: '#555' });
    phBox.appendChild(q);
    this.carouselCardBox.appendChild(phBox);

    // Random info (right)
    const infoBox = document.createElement('div');
    Object.assign(infoBox.style, {
      flex: '1', display: 'flex', flexDirection: 'column',
      justifyContent: 'center',
    });

    const nameEl = document.createElement('div');
    nameEl.textContent = t('mapSelect.randomCharacterLabel');
    Object.assign(nameEl.style, { fontSize: '22px', fontWeight: '700', color: '#e5e9f0' });
    infoBox.appendChild(nameEl);

    const subEl = document.createElement('div');
    subEl.textContent = t('mapSelect.randomFactionLabel');
    Object.assign(subEl.style, { fontSize: '15px', opacity: '0.5', marginTop: '6px' });
    infoBox.appendChild(subEl);

    this.carouselCardBox.appendChild(infoBox);
  }

  /**
   * Open a compact grid popup anchored to (mouseX, mouseY) listing every
   * character from every faction (plus a Random slot). One left-click picks
   * the character for `slot`. Closes on outside click, on another contextmenu,
   * or on Escape.
   */
  private openQuickPickMenu(slot: number, mouseX: number, mouseY: number): void {
    // Scenario-forced factions are read-only: no quick-pick menu.
    if (this.forcedFactionForSlot(slot)) return;
    this.closeQuickPickMenu();

    const overlay = document.createElement('div');
    Object.assign(overlay.style, {
      position: 'fixed', inset: '0', zIndex: '32', pointerEvents: 'none',
    } as Partial<CSSStyleDeclaration>);

    const panel = document.createElement('div');
    Object.assign(panel.style, {
      position: 'absolute',
      background: PANEL_BG, border: '1px solid #2a3140', borderRadius: '6px',
      padding: '6px', maxWidth: '520px', maxHeight: '70vh', overflowY: 'auto',
      boxShadow: '0 6px 20px rgba(0,0,0,0.55)',
      pointerEvents: 'auto',
      display: 'grid', gridTemplateColumns: 'repeat(2, minmax(220px, 1fr))',
      gap: '4px',
      fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
    } as Partial<CSSStyleDeclaration>);

    const currentSel = this.selections[slot];

    const makeRow = (key: string, label: string, flagFactionId: string | null): HTMLButtonElement => {
      const btn = document.createElement('button');
      Object.assign(btn.style, {
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '6px 8px',
        background: key === currentSel ? '#1a2a3a' : 'transparent',
        border: key === currentSel ? '1px solid #4a90e2' : '1px solid transparent',
        borderRadius: '4px', cursor: 'pointer',
        color: '#d8dee9', fontSize: '13px', textAlign: 'left',
        font: 'inherit',
      } as Partial<CSSStyleDeclaration>);
      btn.addEventListener('mouseenter', () => {
        if (key !== currentSel) btn.style.background = '#1a1f28';
      });
      btn.addEventListener('mouseleave', () => {
        if (key !== currentSel) btn.style.background = 'transparent';
      });

      // Flag (or random emoji)
      if (flagFactionId) {
        const flag = createFlagElement(flagFactionId, 28, 20);
        flag.style.flexShrink = '0';
        btn.appendChild(flag);
      } else {
        const ic = document.createElement('div');
        ic.textContent = '🎲';
        Object.assign(ic.style, {
          width: '28px', height: '20px', display: 'flex',
          alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: '0',
        } as Partial<CSSStyleDeclaration>);
        btn.appendChild(ic);
      }

      const lbl = document.createElement('span');
      lbl.textContent = label;
      lbl.style.whiteSpace = 'nowrap';
      lbl.style.overflow = 'hidden';
      lbl.style.textOverflow = 'ellipsis';
      btn.appendChild(lbl);

      btn.addEventListener('click', () => {
        this.selections[slot] = key;
        const isHuman = slot === 0;
        this.renderCardContent(slot, isHuman);
        if (isHuman) this.updateHumanCardGlow();
        this.closeQuickPickMenu();
      });
      btn.addEventListener('contextmenu', (e) => e.preventDefault());
      return btn;
    };

    // Random first
    panel.appendChild(makeRow('random', t('mapSelect.random'), null));

    // All characters of all playable factions
    for (const f of PLAYABLE_FACTIONS) {
      for (const c of f.characters) {
        const charName = t(`faction.${f.id}.${c.id}.name`);
        panel.appendChild(makeRow(`${f.id}:${c.id}`, charName, f.id));
      }
    }

    overlay.appendChild(panel);
    document.body.appendChild(overlay);
    this.quickPickOverlay = overlay;

    // Position the panel near the cursor but keep it on screen.
    requestAnimationFrame(() => {
      const rect = panel.getBoundingClientRect();
      const margin = 8;
      let x = mouseX;
      let y = mouseY;
      if (x + rect.width + margin > window.innerWidth) x = Math.max(margin, window.innerWidth - rect.width - margin);
      if (y + rect.height + margin > window.innerHeight) y = Math.max(margin, window.innerHeight - rect.height - margin);
      panel.style.left = `${x}px`;
      panel.style.top = `${y}px`;
    });

    // Close on outside click / next contextmenu / escape.
    this.quickPickOnDocClick = (e: MouseEvent): void => {
      if (!panel.contains(e.target as Node)) this.closeQuickPickMenu();
    };
    this.quickPickOnEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') this.closeQuickPickMenu();
    };
    // Defer so the same click that opened us isn't immediately the outside click that closes us.
    setTimeout(() => {
      if (this.quickPickOnDocClick) document.addEventListener('mousedown', this.quickPickOnDocClick);
      if (this.quickPickOnDocClick) document.addEventListener('contextmenu', this.quickPickOnDocClick);
      if (this.quickPickOnEsc) document.addEventListener('keydown', this.quickPickOnEsc);
    }, 0);
  }

  private closeQuickPickMenu(): void {
    if (this.quickPickOnDocClick) {
      document.removeEventListener('mousedown', this.quickPickOnDocClick);
      document.removeEventListener('contextmenu', this.quickPickOnDocClick);
      this.quickPickOnDocClick = null;
    }
    if (this.quickPickOnEsc) {
      document.removeEventListener('keydown', this.quickPickOnEsc);
      this.quickPickOnEsc = null;
    }
    this.quickPickOverlay?.remove();
    this.quickPickOverlay = null;
  }

  private closeCarousel(): void {
    if (this.carouselKeyHandler) {
      document.removeEventListener('keydown', this.carouselKeyHandler);
      this.carouselKeyHandler = null;
    }
    this.carouselOverlay?.remove();
    this.carouselOverlay = null;
    this.carouselCardBox = null;
    this.carouselSlot = -1;
  }

  // ─── Start game ───────────────────────────────────────────────

  private startGame(): void {
    const sc = this.selected;
    if (!sc) return;
    const factions = new Map<PlayerId, FactionPick>();

    const takenColors = new Set<number>();
    for (const c of this.colorSelections) {
      if (c >= 0) takenColors.add(c);
    }

    const freeColors: number[] = [];
    for (let i = 0; i < sc.players.length; i++) {
      if (!takenColors.has(i)) freeColors.push(i);
    }
    for (let i = freeColors.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [freeColors[i], freeColors[j]] = [freeColors[j], freeColors[i]];
    }

    let freeIdx = 0;
    const slotToPid: PlayerId[] = [];
    for (let slot = 0; slot < sc.players.length; slot++) {
      if (this.colorSelections[slot] >= 0) {
        slotToPid[slot] = sc.players[this.colorSelections[slot]];
      } else {
        slotToPid[slot] = sc.players[freeColors[freeIdx++]];
      }
    }

    const humanPlayer = slotToPid[0];

    for (let slot = 0; slot < sc.players.length; slot++) {
      const sel = this.selections[slot];
      let factionId: string;
      let characterId: string;

      if (sel === 'random') {
        const pick = PLAYABLE_FACTIONS[Math.floor(Math.random() * PLAYABLE_FACTIONS.length)];
        factionId = pick.id;
        characterId = Math.random() < 0.5 ? 'a' : 'b';
      } else {
        const colonIdx = sel.indexOf(':');
        factionId = sel.slice(0, colonIdx);
        characterId = sel.slice(colonIdx + 1);
      }

      // Scenario-forced factions override the menu pick for the resolved player,
      // regardless of any prior menu state (the picker is read-only anyway).
      const forcedFaction = sc.factions?.[slotToPid[slot]];
      if (forcedFaction) factionId = forcedFaction;

      factions.set(slotToPid[slot], { factionId, characterId: characterId as 'a' | 'b' });
    }

    const config: GameConfig = {
      scenario: sc,
      humanPlayer,
      factions,
    };

    this.scene.start('Loading', { config });
  }

  // ─── Styles ───────────────────────────────────────────────────

  private imgBtnCss(bgImg: string, w: number): string {
    return `display:inline-block;width:${w}px;height:46px;border:none;cursor:pointer;background:url('${bgImg}') no-repeat;background-size:100% 100%;background-color:transparent;color:#E8DCC2;font-family:'Arial Black','Trebuchet MS',Impact,sans-serif;font-weight:bold;font-size:13px;letter-spacing:2px;text-transform:uppercase;line-height:46px;text-align:center;transition:filter 0.15s,transform 0.08s`;
  }

  private teardown(): void {
    this.closeCarousel();
    this.closeQuickPickMenu();
    this.dom?.remove();
    this.dom = null;
    this.glowStyleEl?.remove();
    this.glowStyleEl = null;
    this.humanCard = null;
  }
}
